import { RouterProvider, createRouter } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { NewVersionNotice, Toaster as SuiteToaster, useDarkTheme } from "@optymyse/suite-ui";
import { routeTree } from "./routeTree.gen";
import { retryDelay, retryUnanswered } from "./lib/api";
import { strings } from "./lib/strings";
import { Unavailable } from "./components/ui/states";

/**
 * A page that couldn't be drawn — its code not loaded, offline or after a release replaced it, or anything else that
 * stopped it — said so in place of the page, the frame still there, with a reload that brings the page back.
 */
function PageError() {
  return (
    <div className="mx-auto max-w-3xl px-6 py-12">
      <Unavailable what={strings.states.thisPage} onRetry={() => window.location.reload()} page />
    </div>
  );
}

const router = createRouter({ routeTree, defaultPreload: "intent", defaultErrorComponent: PageError });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// A change that never got an answer is sent again; one Steer answered never is (lib/api.ts). Offline, it waits.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, refetchOnWindowFocus: false, retry: 1 },
    mutations: { retry: retryUnanswered, retryDelay },
  },
});

/** Toasts in the look the page is in now: the person's choice, or the device's. */
function ThemedToaster() {
  const dark = useDarkTheme();
  return (
    <Toaster
      position="bottom-right"
      theme={dark ? "dark" : "light"}
      closeButton
      toastOptions={{
        style: {
          background: "hsl(var(--card))",
          color: "hsl(var(--card-foreground))",
          border: "1px solid hsl(var(--border))",
        },
      }}
    />
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <ThemedToaster />
      {/* The suite's own, for what its pages say (SuiteUi's toast): People's confirmations. */}
      <SuiteToaster />
      {/*
        A new version of Steer, noticed in an open page (SuiteUi's README, "A new version"): it hears every answer of the
        API through the client (lib/api.ts, askApi), and asks the health check, which needs no session, with a HEAD as
        the page opens and when it's shown again, at most once a minute. Beside the router, not in it, so a route that
        can't render doesn't take it away. It gives no `note`: the kit's is for what an app keeps on its server, and Steer
        keeps a comment being written in this browser alone (lib/drafts.ts), which may refuse to keep it.
      */}
      <NewVersionNotice app="Steer" check="/api/v1/health" />
    </QueryClientProvider>
  );
}
